import { useState } from "react";
import type { DropResult } from "react-beautiful-dnd";
import type { ActionCandidate, ActionGroupType } from "../types/candidate";
import * as A from "../stores/automation/mergers/actions";
import type { ActionReference } from "@smartsuite/automation-engine-api";

interface UseActionDragDropProps {
  actions: ActionCandidate[];
  onClickAction: (hash?: string) => void;
  setActions: (
    mergers: A.ActionListMerger[],
    groupType?: ActionGroupType,
  ) => void;
  setActionsWithOutReferences: (
    reference?: string,
    whatActionReferencesRemove?: ActionReference,
  ) => void;
}

interface UseActionDragDropReturn {
  isDragInProgress: boolean;
  handleDragStart: () => void;
  handleDragEnd: (result: DropResult) => void;
}

export const useActionDragDrop = ({
  actions,
  onClickAction,
  setActions,
  setActionsWithOutReferences,
}: UseActionDragDropProps): UseActionDragDropReturn => {
  const [isDragInProgress, setIsDragInProgress] = useState(false);

  const handleDragStart = (): void => {
    setIsDragInProgress(true);
  };

  const handleDragEnd = (result: DropResult): void => {
    setIsDragInProgress(false);
    if (!result.destination) {
      return;
    }

    const actionToMove = actions[result.source.index];

    if (
      actionToMove.recordList?.recordList.oneofKind === "actionReference" &&
      result.destination.index < result.source.index &&
      result.destination.index <=
        actionToMove.recordList.recordList.actionReference.instanceId
    ) {
      const referencedActionIndex = actions.findIndex(
        (action) =>
          actionToMove.recordList?.recordList.oneofKind === "actionReference" &&
          actionToMove.recordList.recordList.actionReference.instanceId ===
            action.actionReference?.instanceId,
      );

      if (
        referencedActionIndex &&
        result.destination.index <= referencedActionIndex
      ) {
        setActions([A.ejectActionReferencesInCurrentAction(actionToMove.hash)]);
      }
    }

    setActions([A.moveAction(result.source.index, result.destination.index)]);
    setActionsWithOutReferences("actionReference");
    onClickAction(actionToMove.hash);
  };

  return {
    isDragInProgress,
    handleDragStart,
    handleDragEnd,
  };
};
